import { useState, useCallback } from 'react';
import { Shape } from '../types/Shape';

export interface UseCanvasEditingProps {
  shapes: Shape[];
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  stagePos: { x: number; y: number };
  stageScale: number;
  canvasContainerRef: React.RefObject<HTMLDivElement | null>;
}

export interface UseCanvasEditingReturn {
  // Inline text editing state
  editingShape: string | null;
  setEditingShape: React.Dispatch<React.SetStateAction<string | null>>;
  editingText: string;
  setEditingText: React.Dispatch<React.SetStateAction<string>>;
  editingPosition: { x: number; y: number; width: number; height: number } | null;
  setEditingPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number; width: number; height: number } | null>>;

  // Inline markdown editing state
  editingMarkdown: string;
  setEditingMarkdown: React.Dispatch<React.SetStateAction<string>>;
  editingMarkdownPosition: { x: number; y: number; width: number; height: number } | null;
  setEditingMarkdownPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number; width: number; height: number } | null>>;

  // Inline code editing state
  editingCode: string;
  setEditingCode: React.Dispatch<React.SetStateAction<string>>;
  editingCodeLanguage: string;
  setEditingCodeLanguage: React.Dispatch<React.SetStateAction<string>>;
  editingCodePosition: { x: number; y: number; width: number; height: number } | null;
  setEditingCodePosition: React.Dispatch<React.SetStateAction<{ x: number; y: number; width: number; height: number } | null>>;

  // Code editor modal state
  isCodeEditorOpen: boolean;
  setIsCodeEditorOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editingCodeShapeId: string | null;
  setEditingCodeShapeId: React.Dispatch<React.SetStateAction<string | null>>;

  // Markdown editor modal state
  isMarkdownEditorOpen: boolean;
  setIsMarkdownEditorOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editingMarkdownShapeId: string | null;
  setEditingMarkdownShapeId: React.Dispatch<React.SetStateAction<string | null>>;

  // Handlers
  handleTextEditComplete: () => void;
  handleTextEditCancel: () => void;
  handleCodeSave: (code: string, language: string) => void;
  handleCodeEditorClose: () => void;
  handleMarkdownSave: (markdown: string) => void;
  handleMarkdownEditorClose: () => void;
  handleDoubleClickToEdit: (shapeId: string) => void;
}

export function useCanvasEditing({
  shapes,
  setShapes,
  setHasUnsavedChanges,
  stagePos,
  stageScale,
  canvasContainerRef,
}: UseCanvasEditingProps): UseCanvasEditingReturn {
  // Inline text editing state
  const [editingShape, setEditingShape] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [editingPosition, setEditingPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  // Inline markdown editing state
  const [editingMarkdown, setEditingMarkdown] = useState('');
  const [editingMarkdownPosition, setEditingMarkdownPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  // Inline code editing state
  const [editingCode, setEditingCode] = useState('');
  const [editingCodeLanguage, setEditingCodeLanguage] = useState('javascript');
  const [editingCodePosition, setEditingCodePosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  // Code editor modal state
  const [isCodeEditorOpen, setIsCodeEditorOpen] = useState(false);
  const [editingCodeShapeId, setEditingCodeShapeId] = useState<string | null>(null);

  // Markdown editor modal state
  const [isMarkdownEditorOpen, setIsMarkdownEditorOpen] = useState(false);
  const [editingMarkdownShapeId, setEditingMarkdownShapeId] = useState<string | null>(null);

  // Handle text edit complete
  const handleTextEditComplete = useCallback(() => {
    if (editingShape) {
      const shape = shapes.find(s => s.id === editingShape);
      if (shape?.type === 'md') {
        // Markdown edit complete
        setShapes(prev =>
          prev.map(s =>
            s.id === editingShape
              ? { ...s, markdown: editingMarkdown }
              : s
          )
        );
        setEditingMarkdown('');
        setEditingMarkdownPosition(null);
      } else if (shape?.type === 'code') {
        // Code edit complete
        setShapes(prev =>
          prev.map(s =>
            s.id === editingShape
              ? { ...s, code: editingCode, language: editingCodeLanguage }
              : s
          )
        );
        setEditingCode('');
        setEditingCodeLanguage('javascript');
        setEditingCodePosition(null);
      } else {
        // Regular text edit complete
        const trimmedText = editingText.trim();

        // autoDeleteIfEmpty 플래그가 있고 텍스트가 비어있으면 삭제
        if (shape?.autoDeleteIfEmpty && !trimmedText) {
          setShapes(prev => prev.filter(s => s.id !== editingShape));
        } else {
          setShapes(prev =>
            prev.map(s =>
              s.id === editingShape
                ? { ...s, label: editingText, text: editingText, autoDeleteIfEmpty: undefined }
                : s
            )
          );
        }
        setEditingText('');
        setEditingPosition(null);
      }
    }
    setEditingShape(null);
    setHasUnsavedChanges(true);
  }, [editingShape, editingText, editingMarkdown, editingCode, editingCodeLanguage, shapes, setShapes, setHasUnsavedChanges]);

  // Handle text edit cancel
  const handleTextEditCancel = useCallback(() => {
    // autoDeleteIfEmpty 플래그가 있는 shape는 취소 시에도 삭제
    if (editingShape) {
      const shape = shapes.find(s => s.id === editingShape);
      if (shape?.autoDeleteIfEmpty) {
        setShapes(prev => prev.filter(s => s.id !== editingShape));
      }
    }

    setEditingShape(null);
    setEditingText('');
    setEditingPosition(null);
    setEditingMarkdown('');
    setEditingMarkdownPosition(null);
    setEditingCode('');
    setEditingCodeLanguage('javascript');
    setEditingCodePosition(null);
  }, [editingShape, shapes, setShapes]);

  // Handle code save from modal
  const handleCodeSave = useCallback((code: string, language: string) => {
    if (editingCodeShapeId) {
      setShapes(prev =>
        prev.map(shape =>
          shape.id === editingCodeShapeId
            ? { ...shape, code, language }
            : shape
        )
      );
      setEditingCodeShapeId(null);
    }
  }, [editingCodeShapeId, setShapes]);

  // Handle code editor close
  const handleCodeEditorClose = useCallback(() => {
    setIsCodeEditorOpen(false);
    setEditingCodeShapeId(null);
  }, []);

  // Handle markdown save from modal
  const handleMarkdownSave = useCallback((markdown: string) => {
    if (editingMarkdownShapeId) {
      setShapes(prev =>
        prev.map(shape =>
          shape.id === editingMarkdownShapeId
            ? { ...shape, markdown }
            : shape
        )
      );
      setEditingMarkdownShapeId(null);
    }
  }, [editingMarkdownShapeId, setShapes]);

  // Handle markdown editor close
  const handleMarkdownEditorClose = useCallback(() => {
    setIsMarkdownEditorOpen(false);
    setEditingMarkdownShapeId(null);
  }, []);

  // Handle double click to start editing
  const handleDoubleClickToEdit = useCallback((shapeId: string) => {
    setEditingShape(shapeId);
    const shape = shapes.find(s => s.id === shapeId);
    if (shape && canvasContainerRef.current) {
      const canvasBox = canvasContainerRef.current.getBoundingClientRect();

      const screenX = shape.x * stageScale + stagePos.x;
      const screenY = shape.y * stageScale + stagePos.y;
      const screenWidth = (shape.width || 100) * stageScale;
      const screenHeight = (shape.height || (shape.radius ? shape.radius * 2 : 100)) * stageScale;

      if (shape.type === 'code') {
        // 코드 블록 편집 - padding 0
        const codePadding = 0;
        setEditingCode(shape.code || '');
        setEditingCodeLanguage(shape.language || 'javascript');
        setEditingCodePosition({
          x: canvasBox.left + screenX + (codePadding * stageScale),
          y: canvasBox.top + screenY + (codePadding * stageScale),
          width: screenWidth - (codePadding * 2 * stageScale),
          height: screenHeight - (codePadding * 2 * stageScale)
        });
      } else if (shape.type === 'md') {
        // 마크다운 편집 - padding 0
        const mdPadding = 0;
        setEditingMarkdown(shape.markdown || '');
        setEditingMarkdownPosition({
          x: canvasBox.left + screenX + (mdPadding * stageScale),
          y: canvasBox.top + screenY + (mdPadding * stageScale),
          width: screenWidth - (mdPadding * 2 * stageScale),
          height: screenHeight - (mdPadding * 2 * stageScale)
        });
      } else {
        // 텍스트 편집 (rect, circle) - padding 8
        const textPadding = 8;
        setEditingText(shape.label || '');
        setEditingPosition({
          x: canvasBox.left + screenX + (textPadding * stageScale),
          y: canvasBox.top + screenY + (textPadding * stageScale),
          width: screenWidth - (textPadding * 2 * stageScale),
          height: screenHeight - (textPadding * 2 * stageScale)
        });
      }
    }
  }, [shapes, stagePos, stageScale, canvasContainerRef]);

  return {
    // Inline text editing state
    editingShape,
    setEditingShape,
    editingText,
    setEditingText,
    editingPosition,
    setEditingPosition,

    // Inline markdown editing state
    editingMarkdown,
    setEditingMarkdown,
    editingMarkdownPosition,
    setEditingMarkdownPosition,

    // Inline code editing state
    editingCode,
    setEditingCode,
    editingCodeLanguage,
    setEditingCodeLanguage,
    editingCodePosition,
    setEditingCodePosition,

    // Code editor modal state
    isCodeEditorOpen,
    setIsCodeEditorOpen,
    editingCodeShapeId,
    setEditingCodeShapeId,

    // Markdown editor modal state
    isMarkdownEditorOpen,
    setIsMarkdownEditorOpen,
    editingMarkdownShapeId,
    setEditingMarkdownShapeId,

    // Handlers
    handleTextEditComplete,
    handleTextEditCancel,
    handleCodeSave,
    handleCodeEditorClose,
    handleMarkdownSave,
    handleMarkdownEditorClose,
    handleDoubleClickToEdit,
  };
}
